import type { Catalogs } from '@patienttrac/i18n';

// Ambulatory (ASC) product catalog. Common vocabulary comes from @patienttrac/i18n `common`;
// scheduler surfaces translate themselves via @patienttrac/scheduler >= 0.5.0.
// ES/FR are Claude-drafted for CLO review.
export const orCatalog: Catalogs = {
  en: {
    "or.brand.tagline": "Ambulatory surgery center operations",
    "or.brand.short": "Facility operations",
    "or.brand.resolving": "Resolving facility…",
    "or.brand.dev_org": "dev org",
    "or.login.signin": "Sign in",
    "or.login.email": "Email",
    "or.login.password": "Password",
    "or.login.continue": "Continue",
    "or.login.forgot": "Forgot password?",
    "or.login.mfa_title": "Two-factor verification",
    "or.login.mfa_hint_prefix": "Enter the 6-digit code from your authenticator app for ",
    "or.login.back": "Back",
    "or.login.verify": "Verify",
    "or.login.footer": "Part of the PatientTrac platform · HIPAA compliant",
    "or.login.err.invalid": "Invalid email or password.",
    "or.login.err.verify_account": "Could not verify your account. Please contact support.",
    "or.login.err.not_registered": "Your account is not registered for PatientTrac Ambulatory. Contact your administrator.",
    "or.login.err.signin_failed": "Sign-in failed.",
    "or.login.err.verify_failed": "Verification failed. Please try again.",
    "or.login.err.invalid_code": "Invalid verification code. Check your authenticator app and try again.",
    "or.login.err.verify_failed_short": "Verification failed",
    "or.mfa.start_failed": "Could not start MFA enrollment: {msg}",
    "or.mfa.verify_failed": "Verification failed: {msg}",
    "or.mfa.code_mismatch": "That code did not match. Make sure your phone time is correct, then try again.",
    "or.mfa.back_signin": "Back to sign in",
    "or.mfa.success": "Two-factor authentication is now active on your account. You’ll be redirected to sign in again with your new code.",
    "or.mfa.title": "Set up two-factor authentication",
    "or.mfa.required": "Required for all PatientTrac Ambulatory users",
    "or.mfa.step1": "Step 1 — Install an authenticator app",
    "or.mfa.step1_body": "Google Authenticator, Authy, 1Password, or Microsoft Authenticator on your phone. Any TOTP-compatible app will work.",
    "or.mfa.step2": "Step 2 — Scan this QR code",
    "or.mfa.qr_unavailable": "QR unavailable — use manual entry",
    "or.mfa.manual": "Can’t scan? Enter this key manually in your authenticator app:",
    "or.mfa.step3": "Step 3 — Enter the code from your app",
    "or.mfa.cancel": "Cancel",
    "or.mfa.verify_enable": "Verify and enable",
    "or.mfa.lost_device": "Lost your device? Contact your administrator to reset MFA.",
    "or.nav.command_center": "Today Command Center",
    "or.nav.schedule": "OR Schedule",
    "or.nav.patient_access": "Patient Access",
    "or.nav.preop": "Pre-Op",
    "or.nav.intraop": "Intra-Op",
    "or.nav.pacu": "PACU",
    "or.nav.documentation": "Documentation",
    "or.nav.send_consent": "Send Consent",
    "or.nav.evidence": "Photos & Evidence",
    "or.nav.preference_cards": "Preference Cards",
    "or.nav.inventory": "Inventory",
    "or.nav.medications": "Medications & DEA",
    "or.nav.equipment": "Equipment",
    "or.nav.reports": "Reports",
    "or.nav.admin": "Administration",
    "or.nav.sign_out": "Sign out",
    "or.mfa.generating": "Generating secure key…",
    "or.mfa.unavailable": "Enrollment unavailable",
  },
  'es-CO': {
    "or.brand.tagline": "Operaciones de centro de cirugía ambulatoria",
    "or.brand.short": "Operaciones de la sede",
    "or.brand.resolving": "Resolviendo sede…",
    "or.brand.dev_org": "org de desarrollo",
    "or.login.signin": "Iniciar sesión",
    "or.login.email": "Correo",
    "or.login.password": "Contraseña",
    "or.login.continue": "Continuar",
    "or.login.forgot": "¿Olvidó su contraseña?",
    "or.login.mfa_title": "Verificación de dos factores",
    "or.login.mfa_hint_prefix": "Ingrese el código de 6 dígitos de su aplicación de autenticación para ",
    "or.login.back": "Atrás",
    "or.login.verify": "Verificar",
    "or.login.footer": "Parte de la plataforma PatientTrac · Cumple con HIPAA",
    "or.login.err.invalid": "Correo o contraseña inválidos.",
    "or.login.err.verify_account": "No se pudo verificar su cuenta. Contacte a soporte.",
    "or.login.err.not_registered": "Su cuenta no está registrada en PatientTrac Ambulatory. Contacte a su administrador.",
    "or.login.err.signin_failed": "No se pudo iniciar sesión.",
    "or.login.err.verify_failed": "La verificación falló. Intente de nuevo.",
    "or.login.err.invalid_code": "Código de verificación inválido. Revise su aplicación de autenticación e intente de nuevo.",
    "or.login.err.verify_failed_short": "La verificación falló",
    "or.mfa.start_failed": "No se pudo iniciar la inscripción MFA: {msg}",
    "or.mfa.verify_failed": "La verificación falló: {msg}",
    "or.mfa.code_mismatch": "Ese código no coincidió. Asegúrese de que la hora de su teléfono sea correcta e intente de nuevo.",
    "or.mfa.back_signin": "Volver a iniciar sesión",
    "or.mfa.success": "La autenticación de dos factores ya está activa en su cuenta. Será redirigido para iniciar sesión de nuevo con su nuevo código.",
    "or.mfa.title": "Configurar la autenticación de dos factores",
    "or.mfa.required": "Obligatoria para todos los usuarios de PatientTrac Ambulatory",
    "or.mfa.step1": "Paso 1 — Instale una aplicación de autenticación",
    "or.mfa.step1_body": "Google Authenticator, Authy, 1Password o Microsoft Authenticator en su teléfono. Cualquier aplicación compatible con TOTP funciona.",
    "or.mfa.step2": "Paso 2 — Escanee este código QR",
    "or.mfa.qr_unavailable": "QR no disponible — use el ingreso manual",
    "or.mfa.manual": "¿No puede escanear? Ingrese esta clave manualmente en su aplicación de autenticación:",
    "or.mfa.step3": "Paso 3 — Ingrese el código de su aplicación",
    "or.mfa.cancel": "Cancelar",
    "or.mfa.verify_enable": "Verificar y activar",
    "or.mfa.lost_device": "¿Perdió su dispositivo? Contacte a su administrador para restablecer el MFA.",
    "or.nav.command_center": "Centro de Mando de Hoy",
    "or.nav.schedule": "Agenda de Quirófanos",
    "or.nav.patient_access": "Acceso de Pacientes",
    "or.nav.preop": "Preoperatorio",
    "or.nav.intraop": "Intraoperatorio",
    "or.nav.pacu": "PACU",
    "or.nav.documentation": "Documentación",
    "or.nav.send_consent": "Enviar Consentimiento",
    "or.nav.evidence": "Fotos y Evidencia",
    "or.nav.preference_cards": "Tarjetas de Preferencia",
    "or.nav.inventory": "Inventario",
    "or.nav.medications": "Medicamentos y DEA",
    "or.nav.equipment": "Equipos",
    "or.nav.reports": "Informes",
    "or.nav.admin": "Administración",
    "or.nav.sign_out": "Cerrar sesión",
    "or.mfa.generating": "Generando clave segura…",
    "or.mfa.unavailable": "Inscripción no disponible",
  },
  fr: {
    "or.brand.tagline": "Opérations de centre de chirurgie ambulatoire",
    "or.brand.short": "Opérations de l'établissement",
    "or.brand.resolving": "Résolution de l'établissement…",
    "or.brand.dev_org": "org de développement",
    "or.login.signin": "Se connecter",
    "or.login.email": "Courriel",
    "or.login.password": "Mot de passe",
    "or.login.continue": "Continuer",
    "or.login.forgot": "Mot de passe oublié ?",
    "or.login.mfa_title": "Vérification à deux facteurs",
    "or.login.mfa_hint_prefix": "Entrez le code à 6 chiffres de votre application d'authentification pour ",
    "or.login.back": "Retour",
    "or.login.verify": "Vérifier",
    "or.login.footer": "Fait partie de la plateforme PatientTrac · Conforme à la HIPAA",
    "or.login.err.invalid": "Courriel ou mot de passe invalide.",
    "or.login.err.verify_account": "Impossible de vérifier votre compte. Veuillez contacter le soutien.",
    "or.login.err.not_registered": "Votre compte n'est pas inscrit à PatientTrac Ambulatory. Contactez votre administrateur.",
    "or.login.err.signin_failed": "Échec de la connexion.",
    "or.login.err.verify_failed": "Échec de la vérification. Veuillez réessayer.",
    "or.login.err.invalid_code": "Code de vérification invalide. Vérifiez votre application d'authentification et réessayez.",
    "or.login.err.verify_failed_short": "Échec de la vérification",
    "or.mfa.start_failed": "Impossible de démarrer l'inscription MFA : {msg}",
    "or.mfa.verify_failed": "Échec de la vérification : {msg}",
    "or.mfa.code_mismatch": "Ce code ne correspond pas. Vérifiez que l'heure de votre téléphone est exacte, puis réessayez.",
    "or.mfa.back_signin": "Retour à la connexion",
    "or.mfa.success": "L'authentification à deux facteurs est maintenant active sur votre compte. Vous serez redirigé pour vous reconnecter avec votre nouveau code.",
    "or.mfa.title": "Configurer l'authentification à deux facteurs",
    "or.mfa.required": "Requise pour tous les utilisateurs de PatientTrac Ambulatory",
    "or.mfa.step1": "Étape 1 — Installez une application d'authentification",
    "or.mfa.step1_body": "Google Authenticator, Authy, 1Password ou Microsoft Authenticator sur votre téléphone. Toute application compatible TOTP fonctionne.",
    "or.mfa.step2": "Étape 2 — Scannez ce code QR",
    "or.mfa.qr_unavailable": "QR indisponible — utilisez la saisie manuelle",
    "or.mfa.manual": "Impossible de scanner ? Entrez cette clé manuellement dans votre application d'authentification :",
    "or.mfa.step3": "Étape 3 — Entrez le code de votre application",
    "or.mfa.cancel": "Annuler",
    "or.mfa.verify_enable": "Vérifier et activer",
    "or.mfa.lost_device": "Appareil perdu ? Contactez votre administrateur pour réinitialiser le MFA.",
    "or.nav.command_center": "Centre de commande du jour",
    "or.nav.schedule": "Horaire des blocs",
    "or.nav.patient_access": "Accès des patients",
    "or.nav.preop": "Préopératoire",
    "or.nav.intraop": "Peropératoire",
    "or.nav.pacu": "PACU",
    "or.nav.documentation": "Documentation",
    "or.nav.send_consent": "Envoyer un consentement",
    "or.nav.evidence": "Photos et preuves",
    "or.nav.preference_cards": "Cartes de préférences",
    "or.nav.inventory": "Inventaire",
    "or.nav.medications": "Médicaments et DEA",
    "or.nav.equipment": "Équipement",
    "or.nav.reports": "Rapports",
    "or.nav.admin": "Administration",
    "or.nav.sign_out": "Se déconnecter",
    "or.mfa.generating": "Génération de la clé sécurisée…",
    "or.mfa.unavailable": "Inscription indisponible",
  },
};
